import { ThemeService, type ThemeOption } from '@/taskpane/services/ThemeService';
import { Check, Monitor, Moon, Sun } from 'lucide-react';
import { useEffect, useRef, useState, type ReactElement } from 'react';

const OPTIONS = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
  { value: 'system', label: 'System', icon: Monitor },
] as const;

export function ThemeMenu(): ReactElement {
  const [theme, setThemeState] = useState<ThemeOption>('system');
  const [resolved, setResolved] = useState<'light' | 'dark'>('light');
  const [isOpen, setIsOpen] = useState(false);
  const containerReference = useRef<HTMLDivElement>(null);

  useEffect(() => {
    ThemeService.applyTheme();
    setThemeState(ThemeService.getTheme());
    setResolved(ThemeService.getResolvedTheme() === 'dark' ? 'dark' : 'light');
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const handlePointer = (event: MouseEvent) => {
      if (!containerReference.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [isOpen]);

  const TriggerIcon = resolved === 'dark' ? Moon : Sun;

  return (
    <div ref={containerReference} className="relative">
      <button
        type="button"
        onClick={() => {
          setIsOpen((open) => !open);
        }}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label="Change theme"
        className="text-muted-foreground hover:text-foreground hover:bg-muted/50 flex size-9 items-center justify-center rounded-full transition-colors"
      >
        <TriggerIcon className="size-4" />
      </button>

      {isOpen ? (
        <div className="border-border bg-popover absolute top-full right-0 z-50 mt-2 min-w-40 overflow-hidden rounded-xl border shadow-2xl">
          <ul className="flex flex-col p-1.5">
            {OPTIONS.map((option) => {
              const isActive = theme === option.value;
              return (
                <li key={option.value}>
                  <button
                    type="button"
                    onClick={() => {
                      ThemeService.setTheme(option.value);
                      setThemeState(option.value);
                      setResolved(ThemeService.getResolvedTheme() === 'dark' ? 'dark' : 'light');
                      setIsOpen(false);
                    }}
                    className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-muted text-foreground'
                        : 'text-muted-foreground hover:bg-muted/70 hover:text-foreground'
                    }`}
                  >
                    <option.icon className={`size-4 shrink-0 ${isActive ? 'text-primary' : ''}`} />
                    <span className="flex-1 text-left">{option.label}</span>
                    {isActive ? <Check className="text-primary size-3.5 shrink-0" /> : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
